// @flow

import gql from 'graphql-tag';

import { useQuery as defaultUseQuery } from '@apollo/react-hooks';
import {
  type ApolloInjection,
  type PaymentSource,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';

type Input = {
  ...$Exact<ApolloInjection>,
  accountId: number,
};

type Output = {
  error: ?string,
  forceSourceEdit: boolean,
  isLoading: boolean,
  paymentSources: Array<PaymentSource>,
  primarySource: ?PaymentSource,
  supportedPaymentMethods: Array<SupportedPaymentMethod>,
};

export const paymentMethodsQuery = gql`
  query paymentMethodQuery($id: Int!) {
    account(id: $id) {
      id
      paymentSources {
        details {
          ... on PaymentSourceBank {
            accountHolderName
            accountType
            addressCity
            addressCountry
            addressState
            addressZip
            bankName
            last4
            ownershipType
            routingNumber
            verified
          }

          ... on PaymentSourceCard {
            addressCity
            addressCountry
            addressState
            addressZip
            brand
            expiry
            last4
            name
          }

          ... on PaymentSourcePaypal {
            email
          }
        }
        id
        method
      }
      supportedPaymentMethodsv2 {
        method
        paymentProcessor
      }
    }
  }
`;

const usePaymentSource = ({
  accountId,
  useQuery = defaultUseQuery,
}: Input): Output => {
  const queryResults = useQuery(paymentMethodsQuery, {
    skip: !accountId,
    variables: {
      id: accountId,
    },
  });

  const paymentSources = queryResults?.data?.account?.paymentSources ?? [];
  const primarySource = paymentSources[0];
  const forceSourceEdit = Boolean(
    primarySource &&
      primarySource.method === paymentSourceMethods.card &&
      !primarySource?.details?.addressCountry
  );

  return {
    error: queryResults.error
      ? 'There was an issue loading your payment sources. Please try again'
      : null,
    forceSourceEdit,
    isLoading: queryResults.loading,
    paymentSources,
    primarySource,
    supportedPaymentMethods:
      queryResults?.data?.account?.supportedPaymentMethodsv2 ?? [],
  };
};

export default usePaymentSource;
